import { Array, Brand, Order, pipe } from 'effect'

export type SqlPredicate = string & Brand.Brand<'SqlPredicate'>
// module-private。raw string からの昇格経路を export せず、
// ユーザー入力由来の文字列が検証なしで SQL 述語になる経路を compile error にする
const SqlPredicate = Brand.nominal<SqlPredicate>()

const IDENTIFIER_PATTERN = /^[A-Za-z_]\w*$/v

// 印字可能 ASCII のみ許可し、バックスラッシュ（0x5C）と制御文字を拒否する。
// バックスラッシュを allowlist で弾いておくと、standard_conforming_strings 設定に依存せず
// シングルクォートの二重化だけで pg の escapeLiteral と同値の安全なリテラルになる
// （core は pg 非依存のため、pg のエスケープ実装を import せず純関数で同値性を保つ）
const VALUE_PATTERN = /^[\u0020-\u005B\u005D-\u007E]*$/v

const quoteIdentifier = (identifier: string): string => {
  if (!IDENTIFIER_PATTERN.test(identifier)) {
    throw new Error(`SqlPredicate: invalid identifier: ${identifier}`)
  }
  return `"${identifier}"`
}

const quoteValue = (value: string): string => {
  if (!VALUE_PATTERN.test(value)) {
    throw new Error(`SqlPredicate: value contains backslash, control or non-ASCII characters`)
  }
  return `'${value.replaceAll(`'`, `''`)}'`
}

// 述語 constructor の出力は必ず `(` で始まる。
// VisibilityDecision の文字列 sentinel 'all' / 'none' と述語の実体 string が
// 値として衝突しないことを、命名規約ではなく出力形で保証する

// 出力例: (("opportunity"."owner_id") IN ('u1', 'u2'))
export const buildColumnInPredicate = (input: {
  table: string
  column: string
  values: Array.NonEmptyReadonlyArray<string>
}): SqlPredicate => {
  // values は昇順 sort + dedupe で正規化する。同一集合なら呼び出し側の並び・重複によらず SQL テキストが
  // 同一になり、生成 SQL のテキストを hash キーとして扱う既存のキャッシュ・上書き照合が構造的に安定する
  const normalizedValues = pipe(input.values, Array.sort(Order.String), Array.dedupe)
  return SqlPredicate(
    `((${quoteIdentifier(input.table)}.${quoteIdentifier(input.column)}) IN (${normalizedValues
      .map((value) => quoteValue(value))
      .join(', ')}))`,
  )
}

// 出力例: ((p1) OR (p2))
export const buildOrPredicate = (predicates: Array.NonEmptyReadonlyArray<SqlPredicate>): SqlPredicate =>
  SqlPredicate(`(${predicates.map((predicate) => `(${predicate})`).join(' OR ')})`)

export const buildFalsePredicate = (): SqlPredicate => SqlPredicate('(FALSE)')

export const buildAndPredicate = (predicates: Array.NonEmptyReadonlyArray<SqlPredicate>): SqlPredicate =>
  SqlPredicate(`(${predicates.map((predicate) => `(${predicate})`).join(' AND ')})`)

export const buildNullCheckPredicate = (input: {
  table: string
  column: string
  operator: 'isNull' | 'isNotNull'
}): SqlPredicate =>
  SqlPredicate(
    `((${quoteIdentifier(input.table)}.${quoteIdentifier(input.column)}) IS ${input.operator === 'isNull' ? '' : 'NOT '}NULL)`,
  )

export const buildCorrelatedExistsPredicate = (input: {
  table: string
  alias: string
  column: string
  outerTable: string
  outerColumn: string
  predicate: SqlPredicate
}): SqlPredicate =>
  SqlPredicate(
    `(EXISTS (SELECT 1 FROM ${quoteIdentifier(input.table)} AS ${quoteIdentifier(input.alias)} WHERE (${quoteIdentifier(input.alias)}.${quoteIdentifier(input.column)}) = (${quoteIdentifier(input.outerTable)}.${quoteIdentifier(input.outerColumn)}) AND (${input.predicate})))`,
  )
